import { Icon, type IconDefinition } from '@vrplatform/icons';
import {
  ActionIcon,
  type ActionIconProps,
  Indicator,
  Tooltip,
  rem,
  useMantineTheme,
} from '@mantine/core';
import type { EmotionSx } from '@mantine/emotion';
import {
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ReactNode,
  forwardRef,
} from 'react';

type Props = {
  count?: number;
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined;
  icon: IconDefinition;
  iconSize?: number;
  size?: ComponentProps<typeof ActionIcon<'button'>>['size'];
  strokeWidth?: number;
  color?: ComponentProps<typeof Icon>['color'];
  tooltip?: ReactNode;
  tooltipMaxWidth?: number;
  variant?: 'default' | 'outline';
  sx?: EmotionSx;
  id?: string;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'draggable' | 'onDragEnd' | 'onDragStart'
> &
  Omit<
    ActionIconProps,
    'onClick' | 'children' | 'color' | 'size' | 'variant' | 'sx'
  >;

const ICON_BUTTON_BADGE_MAX_COUNT = 99;

const getCountLabel = (count?: number): number | string | undefined => {
  if (typeof count !== 'number') return count;
  if (count <= ICON_BUTTON_BADGE_MAX_COUNT) return count;

  return `${ICON_BUTTON_BADGE_MAX_COUNT}+`;
};

type IconButtonActionProps = Omit<
  Props,
  'count' | 'tooltip' | 'tooltipMaxWidth'
> & {
  accessibleLabel?: string;
};

const IconButtonAction = forwardRef<HTMLButtonElement, IconButtonActionProps>(
  (
    {
      accessibleLabel,
      onClick,
      icon,
      variant = 'default',
      iconSize,
      size: buttonSize,
      color,
      strokeWidth,
      sx,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    'use memo';

    const size = iconSize ?? (variant === 'outline' ? 16 : 18);

    return (
      <ActionIcon
        id={id}
        aria-label={accessibleLabel}
        onClick={onClick}
        ref={ref}
        size={buttonSize}
        variant={variant === 'default' ? 'transparent' : 'outline'}
        sx={(theme, u) => ({
          borderRadius: theme.radius.md,
          padding: theme.spacing.xs,
          backgroundColor: disabled
            ? theme.colors.neutral[1]
            : variant === 'outline'
              ? theme.white
              : undefined,
          border: variant === 'outline' ? 'none' : undefined,
          boxShadow:
            variant === 'outline'
              ? '0 1px 2px 0 rgba(42, 59, 81, 0.12), 0 0 0 1px rgba(18, 55, 105, 0.08)'
              : undefined,
          '&:hover': {
            backgroundColor: disabled
              ? theme.colors.neutral[1]
              : variant === 'outline'
                ? `${theme.white}!important`
                : `${theme.colors.gray[0] + 30}!important`,
          },
          ...((typeof sx === 'function' ? sx(theme, u) : sx || {}) as any),
        })}
        disabled={disabled}
        {...props}
      >
        <Icon icon={icon} color={color} size={size} strokeWidth={strokeWidth} />
      </ActionIcon>
    );
  }
);

export const IconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      onClick,
      icon,
      variant = 'default',
      iconSize: is,
      size: buttonSize,
      color,
      count,
      tooltip,
      strokeWidth,
      sx,
      id,
      tooltipMaxWidth,
      disabled,
      ...props
    },
    ref
  ) => {
    'use memo';

    const theme = useMantineTheme();
    const accessibleLabel =
      props['aria-label'] ??
      (typeof tooltip === 'string' ? tooltip : undefined);
    const countLabel = getCountLabel(count);

    return (
      <Indicator
        label={countLabel}
        color={theme.colors.red[9]}
        position="top-end"
        styles={() => ({
          indicator: {
            height: 15,
            minWidth: rem(16),
            paddingInline: rem(3),
            fontSize: 11,
            lineHeight: '15px',
            borderRadius: rem(999),
            whiteSpace: 'nowrap',
          },
        })}
        offset={0}
        disabled={!count}
      >
        <Tooltip
          label={tooltip}
          withArrow
          disabled={
            !tooltip || (typeof tooltip === 'string' && !tooltip?.trim())
          }
          maw={tooltipMaxWidth}
          multiline
        >
          <IconButtonAction
            ref={ref}
            accessibleLabel={accessibleLabel}
            onClick={onClick}
            icon={icon}
            variant={variant}
            iconSize={is}
            size={buttonSize}
            color={color}
            strokeWidth={strokeWidth}
            sx={sx}
            id={id}
            disabled={disabled}
            {...props}
          />
        </Tooltip>
      </Indicator>
    );
  }
);
